import { crossRate, Money } from '@palaccount/money';

/**
 * The web's money helpers — the client-side counterpart of the ledger's
 * discipline (DECISION.md §2.1, PART 18 N-6/N-7/N-13).
 *
 * GEN-100, half enforced and half RECORDED: the leading ASCII minus before
 * the currency sign is N-3's settled format (`-$46,866.91`, the minus-sign
 * wave). The "always coloured red" half is deliberately NOT blanket-applied:
 * the product colours negatives by MEANING (NFR-14) — a negative work-order
 * variance is GREEN (under plan, F-53), a falling purchase price is green
 * (F-98) — so an unconditional red would contradict two proven rules.
 * Screens whose negatives mean trouble colour them red locally.
 *
 * Amounts arrive from the API as NUMERIC strings and must stay strings all the
 * way to the screen. Every screen that needs to ADD, SUBTRACT, COMPARE or
 * ROUND one comes through here, so the arithmetic happens once, in decimal,
 * behind the `money/no-raw-money-arithmetic` fence — instead of `Number(a) +
 * Number(b)` in forty components, which is how a float total reached the till.
 *
 * Two things this module deliberately DOES do with numbers, each named so the
 * exemption is visible rather than assumed:
 *
 *   - `chartValue` / `barPercent` — a pixel width or a chart datum. Those are
 *     geometry, not money: an amount is turned into a number at the very last
 *     step, for layout, and nothing is computed back out of it.
 *   - `compareAmounts` — an Array.sort comparator, which must return a number
 *     by contract. The comparison itself is decimal.
 */

/**
 * The ARITHMETIC TAG these helpers fall back to, and it REACHES NO COLUMN and
 * no screen. `amountOf`/`sumAmounts`/`subtractAmounts` are decimal arithmetic
 * over strings the caller already knows the currency of — a column of figures
 * that are all in the same money, being added — and the tag exists only
 * because `Money` refuses to add across tags. It was `DEFAULT_CURRENCY`,
 * which read like a decision about what money is; nothing here decides that.
 *
 * What a figure is LABELLED with is `formatMoney(value, currency)`, whose
 * currency argument is required, and which is fed `useBaseCurrency()` or the
 * document's own code by the screen (GEN-107 / GEN-108).
 */
const TAG = 'USD';
const DECIMAL_TEXT = /^-?\d+(\.\d+)?$/;

/**
 * A NUMERIC string as Money, tolerantly.
 *
 * Screens hold half-typed input and APIs can hand back nulls; neither may
 * throw in a render. Anything that is not a decimal literal reads as zero,
 * which is the same thing the old `Number(x) || 0` meant — only without the
 * float.
 */
export function amountOf(raw: string | null | undefined, currency = TAG): Money {
  const trimmed = (raw ?? '').trim();
  return DECIMAL_TEXT.test(trimmed) ? Money.of(trimmed, currency) : Money.zero(currency);
}

/** Σ of a column of amounts, as a 2-decimal string. */
export function sumAmounts(values: readonly (string | null | undefined)[], currency = TAG): string {
  return values
    .reduce<Money>((sum, value) => sum.plus(amountOf(value, currency)), Money.zero(currency))
    .toFixed2();
}

/** a − b, as a 2-decimal string. */
export function subtractAmounts(a: string | null | undefined, b: string | null | undefined, currency = TAG): string {
  return amountOf(a, currency).minus(amountOf(b, currency)).toFixed2();
}

export function isPositiveAmount(value: string | null | undefined): boolean {
  return amountOf(value).isPositive();
}

export function isNegativeAmount(value: string | null | undefined): boolean {
  return amountOf(value).isNegative();
}

export function isZeroAmount(value: string | null | undefined): boolean {
  return amountOf(value).isZero();
}

/**
 * §7's convention across the finance screens: zero counts as a GAIN, so the
 * test is "not negative" rather than "greater than zero".
 */
export function isGainOrZero(value: string | null | undefined): boolean {
  return !amountOf(value).isNegative();
}

/**
 * An amount rounded to WHOLE currency units, as a string — BR-DSH-10-15's
 * statement convention ("96,000", never "96,000.00"), before grouping.
 *
 * Half-up through decimal.js, like every other rounding in the product (N-6).
 * `Math.round(Number(v))` would take a float first and rounds a negative half
 * the other way (−0.5 to −0, not to −1).
 */
export function roundToWholeUnits(value: string | null | undefined): string {
  const amount = amountOf(value);
  const negative = amount.isNegative();
  // Half-up at zero decimals: add a half to the magnitude, take the integer
  // part. Every step is decimal; nothing becomes a number.
  const shifted = amount.abs().plus(Money.of('0.5', TAG)).toFixed2();
  const [whole = '0'] = shifted.split('.');
  const trimmed = whole === '' ? '0' : whole;
  return negative && trimmed !== '0' ? `-${trimmed}` : trimmed;
}

/**
 * Wave 3B — `part` as a whole-number percentage of `whole` ("60", "150",
 * never "60.00"), decimal all the way; "0" when the whole is nothing. The
 * list pages' progress lines ("60% paid", "150% billed") read this.
 */
export function percentOf(part: string | null | undefined, whole: string | null | undefined): string {
  const base = amountOf(whole);
  if (!base.isPositive()) return '0';
  const ratio = amountOf(part).times('100').dividedBy(base.toDecimalString());
  return roundToWholeUnits(ratio.toFixed2());
}

/**
 * An `Array.prototype.sort` comparator over amounts, descending or ascending.
 * The comparison is decimal; only the −1/0/1 it must return is a number, which
 * is the sort contract and carries no value.
 */
export function compareAmounts(a: string | null | undefined, b: string | null | undefined): number {
  const left = amountOf(a);
  const right = amountOf(b);
  if (left.lessThan(right)) return -1;
  if (left.greaterThan(right)) return 1;
  return 0;
}

/**
 * An amount as a NUMBER, for a chart library or a CSS width — and for nothing
 * else.
 *
 * This is the one legitimate exit from the decimal world on the client: a
 * bar's height is pixels, and pixels are not money. Nothing may be computed
 * back out of the result and shown as an amount; if a figure is going into a
 * label, it stays a string and goes through `Amount`.
 */
export function chartValue(value: string | null | undefined): number {
  /*
   * The exemption, stated once for the whole module: the result of this line
   * feeds a chart series or a CSS length and is NEVER displayed as money or
   * computed back into one. Everything that will be read as an amount stays a
   * decimal string and goes through `Amount`.
   */
  // eslint-disable-next-line money/no-raw-money-arithmetic -- chart/pixel geometry, not an amount
  return Number(amountOf(value).toFixed2());
}

/**
 * A bar's width as a whole percentage of the widest row, clamped to 0–100.
 * Geometry, like `chartValue` — see its note.
 */
export function barPercent(value: string | null | undefined, max: number): number {
  const magnitude = chartValue(value);
  /* Both operands are pixel figures by now — they came through chartValue. */
  // eslint-disable-next-line money/no-raw-money-arithmetic -- CSS percentage geometry, not an amount
  const pct = max === 0 ? 0 : (magnitude / max) * 100;
  return Math.max(0, Math.min(100, Math.round(pct)));
}

/**
 * The reciprocal of a stored rate at four decimals — "1 EUR = 1.0800 USD"
 * from a stored 0.9259.
 *
 * A rate is not an amount, but it multiplies amounts, so it gets the same
 * treatment: `1 / Number(rate)` is a float, and the fourth decimal of a rate
 * is where a landed cost starts to disagree with the supplier's invoice
 * (FIN-05 §5b warns that an inverted rate "survives arithmetic checking").
 * `crossRate(rate, '1')` is the shared exact-BigInt helper the rest of the
 * product already uses for rate division — reused rather than restated,
 * because "a restatement in the opposite direction produces a 40-times error
 * on a landed cost".
 *
 * Returns null for a rate the table could not have stored (zero, blank, more
 * than four decimals), so the caller can say so rather than print Infinity.
 */
export function inverseRate(rate: string | null | undefined): string | null {
  try {
    return crossRate((rate ?? '').trim(), '1');
  } catch {
    return null;
  }
}

/**
 * Wave 1C — a STORED rate as the rate helpers accept it. The import order's
 * `rate` column is numeric(18,6), so a typed 7.2500 echoes back as
 * "7.250000" — two decimals more than FIN-05 §15's four — and `inverseRate`
 * (rightly) refuses it, which is how the shipment header came to read "—".
 * The column's extra precision is storage width, not information: a rate is
 * TYPED at four decimals (`RateStringSchema` bounds every write), so rounding
 * half-up to four here loses nothing a user ever entered. Done at this call
 * site on purpose — the money package keeps its four-decimal contract.
 *
 * Returns null for a value that is not a decimal at all.
 */
export function displayRate(rate: string | null | undefined): string | null {
  const raw = (rate ?? '').trim();
  if (!/^\d+(\.\d+)?$/.test(raw)) return null;
  // `Money` is the exact decimal; the tag is arithmetic-only and reaches no
  // label. Trailing zeros go so "7.2500" reads as the user typed it.
  const fixed = Money.of(raw, 'USD').toFixed(4);
  return fixed.replace(/\.?0+$/, '');
}
